import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    getTimeClock,
    startTimeClock,
    startBreak,
    resumeWork,
    stopTimeClock,
    discardTimeClock,
} from '../services/TimeClockService';
import { useToast, useConfirm } from '../context/UIContext';
import { extractApiError } from '../utils/apiError';
import '../styles/TimeClock.css';

/** Stempeluhr. Ticks from the elapsed time since the last sync, not from wall-clock timestamps,
 *  so a wrong clock on the employee's machine cannot inflate their time. */

export const fmtClock = (totalSeconds) => {
    const s = Math.max(0, Math.floor(totalSeconds));
    const hh = String(Math.floor(s / 3600)).padStart(2, '0');
    const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
    const ss = String(s % 60).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
};

const fmtMinutes = (minutes) => {
    const m = Math.max(0, Math.round(minutes));
    return m >= 60 ? `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} min` : `${m} min`;
};

/** The break ArbZG §4 requires for an amount of *working* time, which is net of it. */
const legalMinBreak = (workMinutes) => {
    if (workMinutes > 9 * 60) return 45;
    if (workMinutes > 6 * 60) return 30;
    return 0;
};

/** The shortest statutory break that makes a span legal; the thresholds are on working time.
 *  Mirrors `_required_break_for_span`, and a test pins the two together. */
export const requiredBreakMinutes = (grossMinutes) =>
    [0, 30, 45].find(candidate => legalMinBreak(grossMinutes - candidate) <= candidate) ?? 45;

const fmtTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? '—'
        : d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

export default function TimeClock({ onBooked }) {
    const toast = useToast();
    const confirm = useConfirm();

    const [status, setStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [lastStop, setLastStop] = useState(null);
    // Seconds elapsed since the status was fetched, used to tick without polling.
    const [drift, setDrift] = useState(0);
    const syncedAt = useRef(Date.now());

    const applyStatus = useCallback((data) => {
        setStatus(data);
        syncedAt.current = Date.now();
        setDrift(0);
    }, []);

    const load = useCallback(async () => {
        try {
            applyStatus(await getTimeClock());
        } catch {
            setStatus(null);
        } finally {
            setIsLoading(false);
        }
    }, [applyStatus]);

    useEffect(() => { load(); }, [load]);

    // One interval for the whole widget; only runs while something is counting.
    const isCounting = status && status.state !== 'idle';
    useEffect(() => {
        if (!isCounting) return undefined;
        const id = setInterval(() => setDrift((Date.now() - syncedAt.current) / 1000), 1000);
        return () => clearInterval(id);
    }, [isCounting, status]);

    // A tab left open in the background drifts; re-sync when it comes back.
    useEffect(() => {
        const onVisible = () => { if (!document.hidden) load(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [load]);

    const act = async (fn, { confirmText, onDone } = {}) => {
        if (busy) return;
        if (confirmText && !(await confirm(confirmText))) return;
        setBusy(true);
        try {
            const result = await fn();
            if (onDone) onDone(result);
            else applyStatus(result);
        } catch (err) {
            toast(extractApiError(err), 'error');
            load();
        } finally {
            setBusy(false);
        }
    };

    const handleStop = () => act(stopTimeClock, {
        onDone: (result) => {
            setLastStop(result);
            applyStatus({ state: 'idle', work_seconds: 0, break_seconds: 0 });
            const auto = result.auto_break_minutes
                ? ` — ${result.auto_break_minutes} min Pause automatisch abgezogen`
                : '';
            toast(`Tag gebucht: ${result.start_time}–${result.end_time}${auto}`, 'success');
            load();
            if (onBooked) onBooked();
        },
    });

    const handleDiscard = () => act(discardTimeClock, {
        confirmText: 'Laufende Erfassung verwerfen? Die Zeit wird nicht gebucht.',
        onDone: () => {
            toast('Erfassung verworfen', 'info');
            load();
        },
    });

    if (isLoading) return <div className="tc-card tc-card--loading">Stempeluhr wird geladen …</div>;
    if (!status) return null;

    const onBreak = status.state === 'on_break';
    const running = status.state === 'running';
    const idle = status.state === 'idle';

    // The server's counters plus the time that has passed since we fetched them.
    const workSeconds = (status.work_seconds || 0) + (running ? drift : 0);
    const breakSeconds = (status.break_seconds || 0) + (onBreak ? drift : 0);
    const grossMinutes = Math.round((workSeconds + breakSeconds) / 60);
    const required = requiredBreakMinutes(grossMinutes);
    const pendingDeduction = Math.max(0, required - Math.round(breakSeconds / 60));
    const minutesToNextThreshold = grossMinutes <= 6 * 60 ? 6 * 60 - grossMinutes : null;

    return (
        <div className={`tc-card${onBreak ? ' tc-card--break' : ''}${running ? ' tc-card--running' : ''}`}>
            <div className="tc-main">
                <div className="tc-readout">
                    <div className="tc-label">
                        <span className={`tc-dot${running ? ' tc-dot--live' : ''}${onBreak ? ' tc-dot--break' : ''}`} />
                        {idle ? 'Nicht erfasst' : onBreak ? 'Pause läuft' : 'Arbeitszeit läuft'}
                    </div>
                    <div className="tc-time" data-testid="tc-work">{fmtClock(workSeconds)}</div>
                    <div className="tc-meta">
                        {idle ? (
                            status.today_record_id
                                ? `Heute bereits gebucht (${status.today_record_status === 'closed' ? 'abgeschlossen' : 'offen'})`
                                : 'Starte deinen Arbeitstag, wenn du beginnst.'
                        ) : (
                            <>
                                Beginn {fmtTime(status.started_at)}
                                {breakSeconds > 0 && <> · Pause {fmtClock(breakSeconds)}</>}
                            </>
                        )}
                    </div>
                </div>

                <div className="tc-actions">
                    {idle && (
                        <button
                            type="button"
                            className="tc-btn tc-btn--start"
                            onClick={() => act(startTimeClock)}
                            disabled={busy}
                        >
                            Start
                        </button>
                    )}
                    {running && (
                        <>
                            <button type="button" className="tc-btn tc-btn--break" onClick={() => act(startBreak)} disabled={busy}>
                                Pause
                            </button>
                            <button type="button" className="tc-btn tc-btn--stop" onClick={handleStop} disabled={busy}>
                                Stop
                            </button>
                        </>
                    )}
                    {onBreak && (
                        <>
                            <button type="button" className="tc-btn tc-btn--resume" onClick={() => act(resumeWork)} disabled={busy}>
                                Weiter
                            </button>
                            <button type="button" className="tc-btn tc-btn--stop" onClick={handleStop} disabled={busy}>
                                Stop
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Nobody should be surprised by a deduction at the end of the day. */}
            {!idle && (
                <div className="tc-hints">
                    {pendingDeduction > 0 ? (
                        <span className="tc-hint tc-hint--warn">
                            Beim Stopp werden {fmtMinutes(pendingDeduction)} Pause abgezogen —
                            gesetzliche Mindestpause (ArbZG §4) für {fmtMinutes(grossMinutes)} Anwesenheit.
                        </span>
                    ) : required > 0 ? (
                        <span className="tc-hint tc-hint--ok">
                            Mindestpause von {fmtMinutes(required)} ist erfüllt.
                        </span>
                    ) : (
                        <span className="tc-hint">
                            Ab 6 h Anwesenheit sind 30 min Pause Pflicht
                            {minutesToNextThreshold !== null && <> (in {fmtMinutes(minutesToNextThreshold)})</>}.
                        </span>
                    )}
                </div>
            )}

            {status.stale && (
                <div className="tc-stale">
                    <strong>Erfassung vom {status.date} läuft noch.</strong> Ein Arbeitstag kann nicht
                    über Mitternacht gehen — verwirf die Erfassung und trage den Tag von Hand nach.
                    <button type="button" className="tc-btn tc-btn--ghost" onClick={handleDiscard} disabled={busy}>
                        Verwerfen
                    </button>
                </div>
            )}

            {!idle && !status.stale && (
                <button type="button" className="tc-discard" onClick={handleDiscard} disabled={busy}>
                    Erfassung verwerfen
                </button>
            )}

            {lastStop && (
                <div className="tc-summary" role="status">
                    <span className="tc-summary-title">Gebucht</span>
                    <span>{lastStop.start_time}–{lastStop.end_time}</span>
                    <span>Anwesenheit {fmtMinutes(lastStop.gross_minutes)}</span>
                    <span>
                        Pause {fmtMinutes(lastStop.break_minutes)}
                        {lastStop.auto_break_minutes > 0 && ` (davon ${lastStop.auto_break_minutes} min automatisch)`}
                    </span>
                    <span className="tc-summary-net">Arbeitszeit {fmtMinutes(lastStop.net_work_minutes)}</span>
                    {lastStop.extended && <span className="tc-summary-note">Bestehender Tag erweitert</span>}
                </div>
            )}
        </div>
    );
}
